/* eslint-disable react/prop-types */

import React, { useContext, useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import { Link, navigate } from "gatsby";
import { disableBodyScroll } from "body-scroll-lock";
import { DocumentContext } from "~context/DocumentContext";
import { AppContext } from "~context/AppContext";
import Button from "~components/Button";
import Carousel from "~components/Carousel";

const CaseStudies = ({ caseStudies, withLink, className }) => {
  const { navigating, setNavigating } = useContext(AppContext);
  const { windowWidth, windowHeight, device } = useContext(DocumentContext);
  const [hoverId, setHoverId] = useState(null);
  const [transformId, setTransformId] = useState(null);
  const [transformString, setTransformString] = useState(``);

  const renderedCaseStudies = [];

  caseStudies.forEach(({ node }) => {
    node.ref = useRef();

    if (node?.frontmatter?.workpage === true) {
      renderedCaseStudies.push(node);
    }
  });

  renderedCaseStudies.sort((a, b) => {
    const priorityA = a.frontmatter.priority;
    const priorityB = b.frontmatter.priority;

    if (priorityA < priorityB) {
      return -1;
    }

    if (priorityA > priorityB) {
      return 1;
    }

    return 0;
  });

  //

  useEffect(() => {
    if (transformId === null) {
      return;
    }

    const { ref } = renderedCaseStudies[transformId];

    if (!ref?.current) {
      return;
    }

    const { width, height, top, left } = ref.current.getBoundingClientRect();

    // scale maf
    const scaleX = (windowWidth / width) * 1.1;
    const scaleY = (windowHeight / height) * 1.1;

    // translate maf (X)
    const translatePixelsX = windowWidth / 2 - left - width + width / 2;
    const translateX = `${(translatePixelsX / windowWidth) * 100}%`;

    // translate maf (Y)
    const translatePixelsY = 0 - (top - windowHeight / 2 + height / 2);
    const translateY = `${translatePixelsY}px`;

    setTransformString(
      `translateY(${translateY}) scaleX(${scaleX}) scaleY(${scaleY}) translateX(${translateX})`
    );

    disableBodyScroll();
    setNavigating(true);
    setTimeout(
      () => {
        navigate(renderedCaseStudies[transformId].fields.slug);
      },
      device && device !== `desktop` ? 0 : 501
    );
  }, [transformId]);

  //

  const caseStudiesJSX = renderedCaseStudies.map(
    (caseStudy, caseStudyIndex) => {
      const key = `case-study-${caseStudyIndex}`;
      const selected = transformId === caseStudyIndex;
      const indexModulus = caseStudyIndex % 6;

      let gridAlign;

      switch (indexModulus) {
        case 0:
          gridAlign = `grid-end-4 grid-start-2`;
          break;
        case 1:
          gridAlign = `grid-end-6`;
          break;
        case 2:
          gridAlign = `grid-end-4`;
          break;
        case 3:
          gridAlign = `grid-end-4 grid-start-9`;
          break;
        case 4:
          gridAlign = `grid-end-6 grid-start-5`;
          break;
        case 5:
          gridAlign = `grid-end-6 grid-start-2`;
          break;
        default:
          gridAlign = `grid-end-6`;
          break;
      }

      return (
        <div
          ref={caseStudy.ref}
          key={key}
          role="presentation"
          onClick={() => setTransformId(caseStudyIndex)}
          onMouseEnter={() => setHoverId(caseStudyIndex)}
          onMouseLeave={() => setHoverId(null)}
          className={`case-studies__item case-studies__item--${
            caseStudy.frontmatter.imageAspect
          } case-studies__item--${indexModulus} ${gridAlign} xs:w-full relative ${
            selected ? `front` : ``
          } flex items-start justify-start cursor-pointer bg-${
            caseStudy.frontmatter.bannerBackground
          } text-${caseStudy.frontmatter.bannerFontColor}`}
          style={{
            transform: `${
              selected && device === `desktop` ? transformString : ``
            }`
          }}
        >
          <div
            aria-hidden={selected || hoverId !== caseStudyIndex}
            className={`case-studies__item__card ${
              selected || hoverId !== caseStudyIndex
                ? `animation-fade-out-slow`
                : `animation-fade-in-slow`
            } w-full absolute flex items-center justify-center top-0 right-0 bottom-0 left-0 px-v2 py-v2 xs:px-v4 xs:py-v4 bg-${
              caseStudy.frontmatter.bannerBackground
            } text-center`}
          >
            <p
              className={`${
                navigating ? `opacity-0` : ``
              } w-full relative text-${caseStudy.frontmatter.bannerFontColor} ${
                caseStudy.frontmatter.bannerFont
              } f2`}
            >
              {caseStudy.frontmatter.heading}
            </p>
          </div>

          <h3
            aria-hidden={selected || hoverId === caseStudyIndex}
            className={`${navigating ? `opacity-0 hidden` : ``} ${
              selected ||
              (hoverId !== caseStudyIndex && transformId !== indexModulus)
                ? `animation-fade-in-slow`
                : `animation-fade-out-slow`
            } absolute top-0 left-0 z-10 p-v1 xs:p-v3 universal-sans b1 `}
          >
            {caseStudy.frontmatter.title}
          </h3>
        </div>
      );
    }
  );

  return (
    <section
      className={`case-studies ${className} animation-appear-down relative block pt-v8 xs:pt-0 ${
        withLink ? `` : `pb-v8`
      }`}
    >
      {renderedCaseStudies?.[0] &&
        (device !== `mobile` ? (
          <div className="grid items-start">{caseStudiesJSX}</div> // had to remove UL structure to give one point of change for desktop + mobile
        ) : (
          <div className="grid">
            <Carousel
              withDots
              dotColor="black"
              className="grid-end-12 relative"
              items={caseStudiesJSX}
            />
          </div>
        ))}

      {withLink && (
        <section className="grid">
          <div className="case-studies__submit grid-end-12 grid-start-1 mt-v8 flex justify-center">
            <Link to="/work">
              <Button className="" color="orange" filled text="See our work" />
            </Link>
          </div>
        </section>
      )}
    </section>
  );
};

CaseStudies.defaultProps = {
  caseStudies: null,
  filters: {
    industry: `Any`,
    tone: `Any`
  },
  withLink: false
};

CaseStudies.propTypes = {
  caseStudies: PropTypes.arrayOf(PropTypes.shape({})),
  filters: PropTypes.shape({
    industry: PropTypes.string,
    tone: PropTypes.string
  }),
  withLink: PropTypes.bool
};

export default CaseStudies;
